import { Controller } from "@hotwired/stimulus"

// A disappearing post, while it is on the page. The ink keeps lightening as
// the lifetime runs out (the server sets --fade once; this keeps it moving),
// the post is marked as waning in its last stretch, and when the time comes it
// fades from the page and the list closes over the gap. On its own page it
// stays, gone grey, rather than vanishing from under the reader.
//
//   data-disappear-from-value    when it was written (ISO 8601)
//   data-disappear-until-value   when it goes (ISO 8601)
//   data-disappear-remove-value  leave the page at the end (default true)
export default class extends Controller {
  static targets = ["piece"]
  static values = {
    from: String,
    until: String,
    remove: { type: Boolean, default: true }
  }

  // Never past this: the piece stays legible to the end (PiecesHelper::MAX_FADE).
  static MAX_FADE = 0.42

  connect() {
    this.from = new Date(this.fromValue).getTime()
    this.until = new Date(this.untilValue).getTime()
    this.tick()
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  tick() {
    const now = Date.now()
    const left = this.until - now

    if (left <= 0) return this.go()

    const total = Math.max(this.until - this.from, 1)
    const fade = Math.min((now - this.from) / total, 1) * this.constructor.MAX_FADE
    this.pieceTargets.forEach((piece) => piece.style.setProperty("--fade", fade.toFixed(3)))

    // Waning: the last tenth of its life, but never less than a minute nor
    // more than an hour of warning.
    const waning = Math.min(Math.max(total / 10, 60_000), 3_600_000)
    this.element.classList.toggle("waning", left <= waning)

    // Often enough to be smooth, rarely enough to be idle: every second in the
    // last minute, otherwise as the ink changes by a hair.
    const step = left < 60_000 ? 1000 : Math.min(Math.max(total / 400, 5000), 60_000)
    this.timer = setTimeout(() => this.tick(), Math.min(step, left))
  }

  // Its time has come.
  go() {
    if (this.gone) return
    this.gone = true
    this.pieceTargets.forEach((piece) => piece.style.setProperty("--fade", this.constructor.MAX_FADE))
    this.element.classList.remove("waning")

    if (!this.removeValue) {
      this.element.classList.add("gone")
      return
    }

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (still) return this.element.remove()

    // Fade, then close the gap: the height is fixed so it can be eased to
    // nothing, and the margins the list gave it go with it.
    const element = this.element
    element.style.height = `${element.getBoundingClientRect().height}px`
    element.classList.remove("arriving") // an arrival's animation would hold its opacity
    element.classList.add("leaving")
    element.addEventListener("transitionend", function onFaded(event) {
      if (event.propertyName !== "opacity") return
      element.removeEventListener("transitionend", onFaded)
      element.classList.add("left")
      element.style.height = "0px"
      // Margins and padding close at the same time; wait for the height itself,
      // and in any case do not keep an empty article around.
      const done = () => element.remove()
      element.addEventListener("transitionend", (e) => { if (e.propertyName === "height") done() })
      setTimeout(done, 700)
    })
    setTimeout(() => { if (element.isConnected && !element.classList.contains("left")) element.remove() }, 2500)
  }
}
